import { useCallback, useEffect, useRef, useState } from 'react';
import { SkillRequestError } from './client.js';
const EMPTY_LIMITS = { skills: 20, body: 8000 };
const count = (value) => (typeof value === 'number' ? value.toLocaleString('en-US') : 'the allowed number of');
/**
 * A 413's sentence by the field the person sees (gyro-svc userSkills.ts SKILL_LIMITS names): the steps are "Steps", never
 * "body". An unknown limit keeps the service's own sentence.
 */
export function skillLimitText(limit, max, fallback) {
    switch (limit) {
        case 'name': return `Name: at most ${count(max)} characters.`;
        case 'description': return `Description: at most ${count(max)} characters.`;
        case 'aliases': return `Aliases: at most ${count(max)} of them.`;
        case 'alias': return `Aliases: each at most ${count(max)} characters.`;
        case 'body': return `Steps: at most ${count(max)} characters.`;
        case 'skills': return `You already have ${count(max)} skills, the most Gyro keeps per person. Delete one to save another.`;
        case 'storage': return 'Your saved skills are at their storage limit. Delete one, or shorten the steps, to save another.';
        default: return fallback || 'That skill is over one of its limits.';
    }
}
/** A service sentence about one field, with the field named as the person sees it ("body: ..." reads "Steps: ..."). */
function fieldLabelled(sentence) {
    const labels = { body: 'Steps', name: 'Name', description: 'Description', aliases: 'Aliases', attachment: 'File' };
    return sentence.replace(/^(body|name|description|aliases|attachment)(?=[.:])/, (field) => labels[field] ?? field);
}
/** The wording of a failed skill read or change, from the client's error; never the service's raw sentence for a 5xx. */
export function skillErrorText(error, verb = 'read') {
    if (error instanceof SkillRequestError) {
        if (error.custodyRequired)
            return 'Saving skills needs Gyro desktop or the Office pane, where Gyro can verify who you are.';
        if (error.status === 413)
            return skillLimitText(error.limit, error.max, error.detail ? fieldLabelled(error.detail) : '');
        if (error.status === 401)
            return 'Gyro could not confirm who you are here. Sign in again, then try once more.';
        if (error.status === 403 && error.code === 'app_disabled')
            return 'Gyro is turned off for this app.';
        if (error.status === 404 && verb !== 'read')
            return 'That skill is no longer there. It may have been deleted elsewhere.';
        if (error.status === 404)
            return 'Saved skills are not available on this service.';
        if (error.status === 400 && error.detail)
            return fieldLabelled(error.detail);
        if (error.status === 429)
            return 'Too many changes at once. Try again in a moment.';
        if (error.status === 503)
            return 'Saved skills are unavailable for a moment. Try again shortly.';
    }
    return verb === 'save' ? 'The skill could not be saved. Check your connection and try again.'
        : verb === 'delete' ? 'The skill could not be deleted. Check your connection and try again.'
            : 'Your skills could not be loaded. Check your connection and try again.';
}
const byNewest = (left, right) => right.updatedAt.localeCompare(left.updatedAt);
/**
 * GET /v1/skills for the widget's skill controls, read once the status check reports the feature and the panel is on
 * screen, and again on demand (`refresh`) or after each save or delete here. The answer's `verified` is the one fact
 * the controls' write side waits for: a list read on a host that proves only its app key answers verified:false and
 * every "Save" stays hidden (Primary's default 1); nothing is guessed from the surface.
 */
export function useGyroSkills({ client, status, configured, onScreen = true }) {
    const enabled = configured && status !== undefined;
    const [state, setState] = useState('idle');
    const [personal, setPersonal] = useState([]);
    const [company, setCompany] = useState([]);
    const [verified, setVerified] = useState(undefined);
    const [limits, setLimits] = useState(EMPTY_LIMITS);
    const [error, setError] = useState(null);
    const [revision, setRevision] = useState(0);
    const liveRef = useRef(true);
    useEffect(() => { liveRef.current = true; return () => { liveRef.current = false; }; }, []);
    useEffect(() => {
        if (!enabled) {
            setState('idle');
            setPersonal([]);
            setCompany([]);
            setVerified(undefined);
            setError(null);
            return;
        }
        if (!onScreen)
            return;
        let cancelled = false;
        const controller = new AbortController();
        setState((current) => (current === 'ready' ? current : 'loading'));
        setError(null);
        void client.listSkills({ signal: controller.signal }).then((list) => {
            if (cancelled)
                return;
            setPersonal(list.skills.filter((skill) => skill.scope === 'personal').sort(byNewest));
            setCompany(list.skills.filter((skill) => skill.scope === 'company').sort(byNewest));
            setVerified(list.verified);
            setLimits(list.limits);
            setState('ready');
        }).catch((failure) => {
            if (cancelled)
                return;
            setState('error');
            setError(skillErrorText(failure, 'read'));
        });
        return () => { cancelled = true; controller.abort(); };
    }, [client, enabled, onScreen, revision]);
    const refresh = useCallback(() => setRevision((current) => current + 1), []);
    // The service's word on this identity wins over an earlier read: a refused change hides every Save from then on.
    const noteRefusal = useCallback((failure) => {
        if (failure instanceof SkillRequestError && failure.custodyRequired && liveRef.current)
            setVerified(false);
    }, []);
    const get = useCallback(async (id) => {
        try {
            return await client.getSkill(id);
        }
        catch (failure) {
            noteRefusal(failure);
            throw failure;
        }
    }, [client, noteRefusal]);
    const save = useCallback(async (id, input) => {
        try {
            const saved = await client.saveSkill(id, input);
            if (liveRef.current)
                setPersonal((current) => [saved, ...current.filter((skill) => skill.id !== saved.id)].sort(byNewest));
            return saved;
        }
        catch (failure) {
            noteRefusal(failure);
            throw failure;
        }
    }, [client, noteRefusal]);
    const remove = useCallback(async (id) => {
        try {
            const removed = await client.deleteSkill(id);
            // Gone either way: deleted now, or already deleted elsewhere (a 404 answers false).
            if (liveRef.current)
                setPersonal((current) => current.filter((skill) => skill.id !== id));
            return removed;
        }
        catch (failure) {
            noteRefusal(failure);
            throw failure;
        }
    }, [client, noteRefusal]);
    return { state, personal, company, verified, limits, error, refresh, get, save, remove };
}
//# sourceMappingURL=useGyroSkills.js.map